import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Button } from '@/components/ui/button';
import { api } from '@/utils/api';
import { toast } from 'sonner';
import { ArrowLeft, CheckCircle2, XCircle, Award } from 'lucide-react';

export default function Evaluation() {
  const navigate = useNavigate();
  const { courseId } = useParams();
  const [evalData, setEvalData] = useState(null);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.getEvaluation(courseId)
      .then((r) => setEvalData(r.data))
      .catch(() => toast.error('Error al cargar evaluación'));
  }, [courseId]);

  const handleSelect = (qid, optIdx) => {
    setAnswers({ ...answers, [qid]: optIdx });
  };

  const handleSubmit = async () => {
    if (!evalData) return;
    if (Object.keys(answers).length < evalData.questions.length) {
      toast.error('Responde todas las preguntas'); return;
    }
    // Traducir índice mostrado -> índice original (porque las opciones se barajan).
    // Enviamos un dict {question_id: original_index} para evitar problemas con el orden
    // barajado de las preguntas en el servidor.
    const orderedAnswers = {};
    for (const q of evalData.questions) {
      const sel = answers[q.id];
      orderedAnswers[q.id] = q.original_indices[sel];
    }
    setSubmitting(true);
    try {
      const r = await api.submitEvaluation(courseId, orderedAnswers);
      setResult(r.data);
    } catch (e) {
      toast.error('Error al enviar evaluación');
    } finally { setSubmitting(false); }
  };

  if (!evalData) return <DashboardLayout><div className="p-8">Cargando...</div></DashboardLayout>;

  if (result) {
    return (
      <DashboardLayout>
        <div className="max-w-2xl mx-auto" data-testid="evaluation-result">
          <div className={`bg-white rounded-lg shadow-sm border-2 ${result.passed ? 'border-green-500' : 'border-red-500'} p-8 text-center`}>
            {result.passed ? (
              <CheckCircle2 className="h-20 w-20 text-green-600 mx-auto mb-4" />
            ) : (
              <XCircle className="h-20 w-20 text-red-600 mx-auto mb-4" />
            )}
            <h2 className="text-3xl font-bold mb-2">{result.passed ? '¡Aprobado!' : 'No aprobado'}</h2>
            <p className="text-gray-600 mb-4">
              {result.correct_answers} / {result.total_questions} respuestas correctas
            </p>
            <p className="text-5xl font-bold mb-6" style={{ color: result.passed ? '#10b981' : '#DC143C' }}>{result.score}%</p>
            <p className="text-sm text-gray-500 mb-6">Puntaje mínimo: {result.passing_score}%</p>
            <div className="flex gap-3 justify-center">
              <Button variant="outline" onClick={() => navigate('/academia')}>Volver</Button>
              {result.passed && (
                <Button onClick={() => navigate(`/academia/certificado/${courseId}`)} data-testid="view-certificate-button" className="bg-[#DC143C] hover:bg-[#B91C1C] text-white flex items-center gap-2">
                  <Award className="h-4 w-4" /> Ver Diploma
                </Button>
              )}
            </div>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-3xl mx-auto" data-testid="evaluation-page">
        <Button variant="ghost" onClick={() => navigate('/academia')} className="mb-4 flex items-center gap-2">
          <ArrowLeft className="h-4 w-4" /> Volver
        </Button>
        <h1 className="text-2xl font-bold mb-2">{evalData.course_title}</h1>
        <p className="text-gray-600 mb-6">Evaluación · Mínimo para aprobar: {evalData.passing_score}%</p>

        <div className="space-y-4">
          {evalData.questions.map((q, qi) => (
            <div key={q.id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-5" data-testid="evaluation-question">
              <p className="font-medium text-gray-900 mb-3">{qi + 1}. {q.question_text}</p>
              <div className="space-y-2">
                {q.options.map((opt, oi) => (
                  <label key={oi} className={`flex items-center gap-3 p-3 rounded border cursor-pointer transition-colors ${
                    answers[q.id] === oi ? 'border-[#DC143C] bg-red-50' : 'border-gray-200 hover:bg-gray-50'
                  }`}>
                    <input
                      type="radio"
                      name={`q-${q.id}`}
                      checked={answers[q.id] === oi}
                      onChange={() => handleSelect(q.id, oi)}
                      className="h-4 w-4"
                    />
                    <span className="text-sm">{opt}</span>
                  </label>
                ))}
              </div>
            </div>
          ))}
        </div>

        <Button onClick={handleSubmit} disabled={submitting} data-testid="submit-evaluation-button" className="mt-6 w-full bg-[#DC143C] hover:bg-[#B91C1C] text-white py-3 font-medium uppercase">
          {submitting ? 'Enviando...' : 'Enviar Evaluación'}
        </Button>
      </div>
    </DashboardLayout>
  );
}
